import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const PermissionsContext = createContext({
  loading: true,
  role: undefined,
  roles: [],
  permissions: [],
  isSystemAdmin: false,
  hasPermission: () => false,
});

export const PermissionsProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, role: undefined, roles: [], permissions: [], isSystemAdmin: false });
  const [version, setVersion] = useState(0);
  const [rolePreview, setRolePreview] = useState(() => {
    try { return localStorage.getItem('hrapp.rolePreview') || ''; } catch { return ''; }
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const prof = await api.get('/Auth/profile');
        if (!mounted) return;
        const roles = (prof?.roles || []).map(r => r.name || r.Name || r);
        const role = roles && roles.length > 0 ? roles[0] : (prof?.role || 'Employee');
        const permissions = prof?.permissions || [];
        const isSystemAdmin = !!(prof?.summary?.isSystemAdmin) || role === 'SystemAdmin';
        setState({ loading: false, role, roles, permissions, isSystemAdmin });
      } catch (e) {
        setState(s => ({ ...s, loading: false }));
        console.warn('Permissions profile load failed', e);
      }
    })();
    return () => { mounted = false; };
  }, [version]);

  useEffect(() => {
    const handler = ()=> setVersion(v=>v+1);
    window.addEventListener('hrapp.permissions.refresh', handler);
    return ()=> window.removeEventListener('hrapp.permissions.refresh', handler);
  }, []);

  useEffect(() => {
    try {
      if (rolePreview) localStorage.setItem('hrapp.rolePreview', rolePreview);
      else localStorage.removeItem('hrapp.rolePreview');
      window.__rolePreview = rolePreview;
      window.dispatchEvent(new Event('hrapp.permissions.refresh'));
    } catch {}
  }, [rolePreview]);

  const value = useMemo(() => {
    // Effective roles for UI when preview is set
    const effectiveRoles = rolePreview ? [rolePreview] : state.roles;
    const effectiveIsSysAdmin = rolePreview ? false : state.isSystemAdmin;
    const effectiveRole = effectiveRoles && effectiveRoles.length > 0 ? effectiveRoles[0] : state.role;

    return {
      // Effective values used across UI
      loading: state.loading,
      role: effectiveRole,
      roles: effectiveRoles,
      permissions: rolePreview ? [] : state.permissions, // in preview we hide permission-based UI and rely on role-based UI
      isSystemAdmin: effectiveIsSysAdmin,

      // Raw (real) values from profile
      realRoles: state.roles,
      realIsSystemAdmin: state.isSystemAdmin,

      // Helpers
      hasPermission: (p) => (rolePreview ? false : (effectiveIsSysAdmin || state.permissions.includes(p))),
      refresh: () => setVersion(v => v + 1),

      // Preview controls (SysAdmin UI only)
      rolePreview,
      setRolePreview,
    };
  }, [state, rolePreview]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);

